import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import ErrorBoundary from "@/components/ErrorBoundary";
import { Toaster } from "sonner";
import { ConfirmProvider } from "@/components/ConfirmDialog";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { LangProvider } from "@/context/LangContext";
import Layout from "@/components/Layout";
import Home from "@/pages/Home";
import Community from "@/pages/Community";
import CommunityDetail from "@/pages/CommunityDetail";
import GroupCreate from "@/pages/GroupCreate";
import Events from "@/pages/Events";
import EventDetail from "@/pages/EventDetail";
import Saved from "@/pages/Saved";
import OrganizerEvents from "@/pages/OrganizerEvents";
import MyTickets from "@/pages/MyTickets";
import TicketView from "@/pages/TicketView";
import TicketScanner from "@/pages/TicketScanner";
import AdminTickets from "@/pages/AdminTickets";
import { FavoritesProvider } from "@/components/FavoriteButton";
import BusinessProfile from "@/pages/directory/BusinessProfile";
import Jobs from "@/pages/Jobs";
import Explore from "@/pages/Explore";
import Feed from "@/pages/Feed";
import Publish from "@/pages/Publish";
import Messages from "@/pages/Messages";
import Profile from "@/pages/Profile";
import MemberProfile from "@/pages/MemberProfile";
import MyPosts from "@/pages/MyPosts";
import PostPage from "@/pages/PostPage";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Admin from "@/pages/Admin";
import AdminFeatures from "@/pages/AdminFeatures";
import AdminVideos from "@/pages/AdminVideos";
import Legal from "@/pages/Legal";
import Refunds from "@/pages/Refunds";
import PaymentSuccess from "@/pages/PaymentSuccess";
import MyFeatured from "@/pages/MyFeatured";
import PaymentCancel from "@/pages/PaymentCancel";
import NativeBridge from "@/components/NativeBridge";
import Marketplace from "@/pages/marketplace/Marketplace";
import ProductDetail from "@/pages/marketplace/ProductDetail";
import SellerRedirect from "@/pages/marketplace/SellerRedirect";
import Cart from "@/pages/marketplace/Cart";
import MyOrders from "@/pages/marketplace/MyOrders";
import Sell, { SellerApply } from "@/pages/marketplace/Sell";
import ProductForm from "@/pages/marketplace/ProductForm";
import AdminMarketplace from "@/pages/marketplace/AdminMarketplace";
import SellerPlans from "@/pages/marketplace/SellerPlans";
import Services from "@/pages/marketplace/Services";
import ServiceDetail from "@/pages/marketplace/ServiceDetail";
import ServiceForm from "@/pages/marketplace/ServiceForm";
import MyQuotes from "@/pages/marketplace/Quotes";
import Verification from "@/pages/marketplace/Verification";
import Restaurants, { RestaurantDetail, MyReservations } from "@/pages/Restaurants";
import Notifications from "@/pages/Notifications";
import Conversation from "@/pages/Conversation";
import NotFound from "@/pages/NotFound";
import VerifyEmail from "@/pages/VerifyEmail";

function ProtectedRoute({ children, admin = false }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-zinc-500">A carregar…</div>;
  if (!user) return <Navigate to={`/login?next=${encodeURIComponent(window.location.pathname)}`} replace />;
  if (admin && user.role !== "admin") return <Navigate to="/" replace />;
  return children;
}

function RouteBoundary({ children }) {
  const location = useLocation();
  return <ErrorBoundary location={location.pathname}>{children}</ErrorBoundary>;
}

function App() {
  return (
    <LangProvider>
      <AuthProvider>
        <FavoritesProvider>
        <ConfirmProvider>
        <BrowserRouter>
          <NativeBridge />
          <Toaster position="top-center" richColors />
          <RouteBoundary>
          <Routes>
            <Route element={<Layout />}>
              <Route path="/" element={<Home />} />
              <Route path="/comunidade" element={<Community />} />
              <Route path="/comunidade/:id" element={<CommunityDetail />} />
              <Route path="/grupos/novo" element={<ProtectedRoute><GroupCreate /></ProtectedRoute>} />
              <Route path="/grupos/:slug" element={<CommunityDetail />} />
              <Route path="/eventos" element={<Events />} />
              <Route path="/eventos/organizador" element={<OrganizerEvents />} />
              <Route path="/eventos/:id/entradas" element={<ProtectedRoute><TicketScanner /></ProtectedRoute>} />
              <Route path="/bilhetes" element={<ProtectedRoute><MyTickets /></ProtectedRoute>} />
              <Route path="/bilhete/:code" element={<ProtectedRoute><TicketView /></ProtectedRoute>} />
              <Route path="/admin/bilhetes" element={<ProtectedRoute admin><AdminTickets /></ProtectedRoute>} />
              <Route path="/guardados" element={<Saved />} />
              <Route path="/eventos/:id" element={<EventDetail />} />
              <Route path="/negocios" element={<Navigate to="/marketplace" replace />} />
              <Route path="/negocios/:id" element={<BusinessProfile />} />
              <Route path="/empregos" element={<Jobs />} />
              <Route path="/classificados" element={<Navigate to="/explorar" replace />} />
              <Route path="/explorar" element={<Explore />} />
              <Route path="/feed" element={<ProtectedRoute><Feed /></ProtectedRoute>} />
              <Route path="/mensagens" element={<Messages />} />
              <Route path="/mensagens/:id" element={<ProtectedRoute><Conversation /></ProtectedRoute>} />
              <Route path="/publicar" element={<ProtectedRoute><Publish /></ProtectedRoute>} />
              <Route path="/perfil" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
              <Route path="/perfil/publicacoes" element={<ProtectedRoute><MyPosts /></ProtectedRoute>} />
              <Route path="/publicacao/:id" element={<ProtectedRoute><PostPage /></ProtectedRoute>} />
              <Route path="/membro/:id" element={<ProtectedRoute><MemberProfile /></ProtectedRoute>} />
              <Route path="/notificacoes" element={<ProtectedRoute><Notifications /></ProtectedRoute>} />
              <Route path="/admin" element={<ProtectedRoute admin><Admin /></ProtectedRoute>} />
              <Route path="/admin/destaques" element={<ProtectedRoute admin><AdminFeatures /></ProtectedRoute>} />
              <Route path="/admin/videos" element={<ProtectedRoute admin><AdminVideos /></ProtectedRoute>} />
              <Route path="/admin/marketplace" element={<ProtectedRoute admin><AdminMarketplace /></ProtectedRoute>} />
              <Route path="/marketplace" element={<Marketplace />} />
              <Route path="/marketplace/produto/:id" element={<ProductDetail />} />
              <Route path="/marketplace/lojas" element={<Navigate to="/marketplace" replace />} />
              <Route path="/marketplace/loja/:id" element={<SellerRedirect />} />
              <Route path="/marketplace/carrinho" element={<ProtectedRoute><Cart /></ProtectedRoute>} />
              <Route path="/marketplace/compras" element={<ProtectedRoute><MyOrders /></ProtectedRoute>} />
              <Route path="/vender" element={<ProtectedRoute><Sell /></ProtectedRoute>} />
              <Route path="/vender/loja" element={<ProtectedRoute><SellerApply editing /></ProtectedRoute>} />
              <Route path="/vender/planos" element={<ProtectedRoute><SellerPlans /></ProtectedRoute>} />
              <Route path="/vender/produto/:id" element={<ProtectedRoute><ProductForm /></ProtectedRoute>} />
              <Route path="/vender/servico/:id" element={<ProtectedRoute><ServiceForm /></ProtectedRoute>} />
              <Route path="/vender/verificacao" element={<ProtectedRoute><Verification /></ProtectedRoute>} />
              <Route path="/restaurantes" element={<Restaurants />} />
              <Route path="/restaurante/:id" element={<RestaurantDetail />} />
              <Route path="/minhas-reservas" element={<ProtectedRoute><MyReservations /></ProtectedRoute>} />
              <Route path="/marketplace/servicos" element={<Services />} />
              <Route path="/marketplace/servico/:id" element={<ServiceDetail />} />
              <Route path="/marketplace/orcamentos" element={<ProtectedRoute><MyQuotes /></ProtectedRoute>} />
              <Route path="/login" element={<Login />} />
              <Route path="/registar" element={<Register />} />
              <Route path="/esqueci" element={<ForgotPassword />} />
              <Route path="/reset-password/:token" element={<ResetPassword />} />
              <Route path="/verificar-email/:token" element={<VerifyEmail />} />
              <Route path="/termos" element={<Legal />} />
              <Route path="/regras" element={<Legal />} />
              <Route path="/privacidade" element={<Legal />} />
              <Route path="/reembolsos" element={<Refunds />} />
              <Route path="/pagamento/sucesso" element={<PaymentSuccess />} />
              <Route path="/destaques/sucesso" element={<PaymentSuccess />} />
              <Route path="/destaques" element={<ProtectedRoute><MyFeatured /></ProtectedRoute>} />
              <Route path="/pagamento/cancelado" element={<PaymentCancel />} />
              <Route path="*" element={<NotFound />} />
            </Route>
          </Routes>
          </RouteBoundary>
        </BrowserRouter>
        </ConfirmProvider>
        </FavoritesProvider>
      </AuthProvider>
    </LangProvider>
  );
}

export default App;
